import styled from "@emotion/styled";
import { DayOfWeek, LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import React from "react";
import { formatDate, formatDayOfWeek, formatTime } from "../../utils/dateUtils";
import Flex from "../Generic/Flex";

export interface PatternRequestDetails extends BaseRequestDetails {
  type: "Pattern";
  startDate: LocalDate;
  endDate: LocalDate;
  days: {
    day: DayOfWeek;
    startTime: LocalTime;
    endTime: LocalTime;
  }[];
}

export interface SinglesRequestDetails extends BaseRequestDetails {
  type: "Singles";
  instances: {
    startDateTime: LocalDateTime;
    endDateTime: LocalDateTime;
  }[];
}

interface BaseRequestDetails {
  address: string;
  visitRate: number | null;
}

interface Props {
  requestDetails: SinglesRequestDetails | PatternRequestDetails;
}

const MedflytRequestNonFlexibleVisitDetails = (props: Props) => {
  return (
    <>
      {props.requestDetails.type === "Pattern" ? (
        <RequestPatternVisitDetails
          visitStartDate={props.requestDetails.startDate}
          visitEndDate={props.requestDetails.endDate}
          request={props.requestDetails.days}
        />
      ) : (
        <RequestSingleVisitDetails request={props.requestDetails.instances} />
      )}
    </>
  );
};

export default MedflytRequestNonFlexibleVisitDetails;

interface RequestSingleVisitDetailsProps {
  request: {
    startDateTime: LocalDateTime;
    endDateTime: LocalDateTime;
  }[];
}

const RequestSingleVisitDetails = (props: RequestSingleVisitDetailsProps) => {
  return (
    <RequestContainer>
      {props.request.map((instance) => {
        const startDate = instance.startDateTime.toLocalDate();
        const startTime = instance.startDateTime.toLocalTime();
        const endTime = instance.endDateTime.toLocalTime();

        return (
          <SingleRequestedShiftRow key={`shift-${startDate.toString()}`}>
            <div>{formatDate(startDate)}</div>
            <RequestedTimes>
              <div>{`${formatTime(startTime)}`}</div>
              <div>{`-`}</div>
              <div>{`${formatTime(endTime)}`}</div>
            </RequestedTimes>
          </SingleRequestedShiftRow>
        );
      })}
    </RequestContainer>
  );
};

interface RequestPatternVisitDetailsProps {
  visitStartDate: LocalDate;
  visitEndDate: LocalDate;
  request: {
    day: DayOfWeek;
    startTime: LocalTime;
    endTime: LocalTime;
  }[];
}

const RequestPatternVisitDetails = (props: RequestPatternVisitDetailsProps) => {
  return (
    <Flex direction="column" gap={8}>
      <Flex direction="row" gap={8}>
        <div> {`${formatDate(props.visitStartDate)}`}</div>
        <div> {`-`}</div>
        <div> {`${formatDate(props.visitEndDate)}`}</div>
      </Flex>
      {props.request
        .sort((shift1, shift2) => shift1.day.ordinal() - shift2.day.ordinal())
        .map((day) => {
          return (
            <PatternRequestedShiftRow key={`shift-${day.day.ordinal()}`}>
              <div>{formatDayOfWeek(day.day)}</div>
              <RequestedTimes>
                <div>{`${formatTime(day.startTime)} `}</div>
                <div>{`-`}</div>
                <div>{`${formatTime(day.endTime)}`}</div>
              </RequestedTimes>
            </PatternRequestedShiftRow>
          );
        })}
    </Flex>
  );
};

const RequestContainer = styled.div`
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
`;

const PatternRequestedShiftRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
`;

const SingleRequestedShiftRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
`;

const RequestedTimes = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  justify-content: space-between;
`;
